/**
 * Bottom drawer of the Agent Composer: run parameters, live event log, and
 * accumulating workflow state inspector.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import { Play, Square, ChevronDown, ChevronUp, MessageSquare } from 'lucide-react';
import type { RunLogEntry } from '../../types/composer';
import { ROUTES } from '../../utils/routes';

export interface ComposerRunPanelProps {
  status: string | null;
  isRunning: boolean;
  executionId: string | null;
  log: RunLogEntry[];
  state: Record<string, unknown>;
  errorMessage: string | null;
  canRun: boolean;
  /** When true, an empty input blocks Run (graph references {{input}}). */
  requiresInput: boolean;
  onRun: (input: string) => void;
  onStop: () => void;
}

export const ComposerRunPanel = ({
  status,
  isRunning,
  executionId,
  log,
  state,
  errorMessage,
  canRun,
  requiresInput,
  onRun,
  onStop,
}: ComposerRunPanelProps) => {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  const tryRun = () => {
    if (requiresInput && !input.trim()) {
      setInputError('This workflow uses {{input}} — an input value is required.');
      return;
    }
    setInputError(null);
    onRun(input);
  };

  const statusClass = status ? `status-${status.toLowerCase()}` : '';
  // The engine puts the aggregated multi-agent parent session id into the
  // workflow state; when present, offer a direct jump to that conversation.
  const parentSessionId =
    typeof state.parentSessionId === 'string' && state.parentSessionId ? state.parentSessionId : null;

  return (
    <section className="cx-runpanel" aria-label="Run panel">
      <div className="cx-runpanel-bar">
        <button
          type="button"
          className="cx-btn cx-btn-run"
          disabled={!canRun || isRunning}
          onClick={tryRun}
          aria-label="Run workflow"
        >
          <Play size={14} />
          Run
        </button>
        <button
          type="button"
          className="cx-btn cx-btn-ghost"
          disabled={!isRunning}
          onClick={onStop}
          aria-label="Stop watching"
        >
          <Square size={14} />
          Stop
        </button>

        <label htmlFor="workflow-input" className="cx-sr-only">Workflow input</label>
        <div className="flex flex-1 flex-col" style={{ maxWidth: 420 }}>
          <Input
            id="workflow-input"
            type="text"
            className={`w-full ${inputError ? 'border-red-500' : ''}`}
            placeholder="input → {{input}}"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              if (inputError) setInputError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canRun && !isRunning) {
                tryRun();
              }
            }}
          />
          {inputError && <p className="mt-1 text-xs text-red-500">{inputError}</p>}
        </div>

        <span className={`cx-status-chip ${statusClass}`} role="status">
          {status ?? 'idle'}
        </span>

        {executionId ? (
          <span
            style={{
              fontFamily: 'var(--cx-mono)',
              fontSize: '0.62rem',
              color: 'var(--cx-ink-faint)',
            }}
            title={executionId}
          >
            {executionId.slice(0, 8)}…
          </span>
        ) : null}

        {parentSessionId ? (
          <button
            type="button"
            className="cx-btn cx-btn-ghost"
            onClick={() => navigate(ROUTES.SESSION_DETAIL.replace(':id', parentSessionId))}
            aria-label="Open the aggregated workflow session"
          >
            <MessageSquare size={14} />
            Open session
          </button>
        ) : null}

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCollapsed((v) => !v)}
                aria-label={collapsed ? 'Expand run panel' : 'Collapse run panel'}
                className="ml-auto text-muted-foreground"
              >
                {collapsed ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{collapsed ? 'Expand run panel' : 'Collapse run panel'}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {!collapsed && (
        <div className="cx-runpanel-columns">
          <section className="cx-runpanel-col" aria-labelledby="eventlog-heading">
            <h3 id="eventlog-heading" className="cx-runpanel-col-title">Event log</h3>
            {errorMessage ? (
              <div className="cx-log-row kind-error" style={{ marginBottom: 8 }} role="alert">
                <span className="cx-log-msg">{errorMessage}</span>
              </div>
            ) : null}
            <div className="cx-log" role="log" aria-live="polite" aria-relevant="additions">
              {log.length === 0 ? (
                <div style={{ color: 'var(--cx-ink-faint)' }}>No events yet. Hit Run to start.</div>
              ) : (
                log.map((entry) => (
                  <div key={entry.id} className={`cx-log-row kind-${entry.kind}`}>
                    <span className="cx-log-time">{formatTime(entry.at)}</span>
                    <span className="cx-log-msg">{entry.message}</span>
                  </div>
                ))
              )}
            </div>
          </section>

          <section className="cx-runpanel-col" aria-labelledby="state-heading">
            <h3 id="state-heading" className="cx-runpanel-col-title">State</h3>
            {Object.keys(state).length === 0 ? (
              <div style={{ color: 'var(--cx-ink-faint)', fontFamily: 'var(--cx-mono)', fontSize: '0.68rem' }}>
                State accumulates as nodes complete (output_&lt;nodeId&gt;, sessionId_&lt;nodeId&gt;, …).
              </div>
            ) : (
              Object.entries(state).map(([key, value]) => (
                <div key={key} className="cx-state-entry">
                  <div className="cx-state-key">{key}</div>
                  <pre className="cx-state-value">{formatValue(value)}</pre>
                </div>
              ))
            )}
          </section>
        </div>
      )}
    </section>
  );
};

const formatTime = (iso: string) => {
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour12: false });
  } catch {
    return iso;
  }
};

const formatValue = (value: unknown): string => {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};
